import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import useDataStore from "../../stores/useDataStore";
import { X } from "lucide-react";
import { useRef } from "react";

const AddTrainerModal = ({ open, onClose, trainerToEdit }) => {
    const { createTrainer, updateTrainer } = useDataStore();
    const fileInputRef = useRef();

    const formik = useFormik({
        initialValues: {
            name: trainerToEdit?.name || { en: "", ar: "" },
            title: trainerToEdit?.title || { en: "", ar: "" },
            description: trainerToEdit?.description || { en: "", ar: "" },
            profileImage: trainerToEdit?.profileImage || "",
            socialMedia: trainerToEdit?.socialMedia || {
                facebook: "",
                instagram: "",
                linkedin: "",
                twitter: "",
            },
        },
        validationSchema: Yup.object({
            name: Yup.object({
                en: Yup.string().required("Required"),
                ar: Yup.string().required("مطلوب"),
            }),
            title: Yup.object({
                en: Yup.string().required("Required"),
                ar: Yup.string().required("مطلوب"),
            }),
            description: Yup.object({
                en: Yup.string().required("Required"),
                ar: Yup.string().required("مطلوب"),
            }),
            profileImage: Yup.string().required("Profile image is required"),
            socialMedia: Yup.object({
                facebook: Yup.string().url("Must be a valid URL").nullable(),
                instagram: Yup.string().url("Must be a valid URL").nullable(),
                linkedin: Yup.string().url("Must be a valid URL").nullable(),
                twitter: Yup.string().url("Must be a valid URL").nullable(),
            }),
        }),
 

        onSubmit: async (values, { resetForm }) => {
            let response;
            if (trainerToEdit?._id) {
                console.log(trainerToEdit?._id);

                // تحديث الكورس
                response = await updateTrainer(trainerToEdit._id, values);
                if (response) {
                    toast.success("Trainer updated successfully!");
                }
                else toast.error("Failed to update Trainer.");
            } else {
                // إنشاء كورس جديد
                response = await createTrainer(values);
                if (response) {
                    toast.success("Trainer created successfully!");
                    resetForm();
                } else toast.error("Failed to create Trainer.");
            }
            if (response) onClose();
        },

    });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue("profileImage", reader.result);
                toast.success("Image loaded successfully");
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue("profileImage", reader.result);
                toast.success("Image loaded successfully");
            };
            reader.readAsDataURL(file);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-auto">
            <button
                onClick={onClose}
                className="absolute top-10 right-10 text-white hover:text-gray-400"
            >
                <X className="h-6 w-6" />
            </button>
            <div className="relative bg-white rounded-lg shadow-xl w-11/12 max-w-screen-xl max-h-[90vh] overflow-y-auto p-6">

                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <h1 className="text-3xl font-bold text-center mb-6">{trainerToEdit ? "Edit Trainer" : "Add Trainer"}</h1>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name */}
                        <div>
                            <label>Name (EN)</label>
                            <input
                                type="text"
                                name="name.en"
                                value={formik.values.name.en}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="input p-2 my-1 border shadow-md rounded-md w-full"
                            />
                            {formik.touched.name?.en && formik.errors.name?.en && (
                                <p className="text-red-500">{formik.errors.name.en}</p>
                            )}
                        </div>
                        <div>
                            <label>الاسم (AR)</label>
                            <input
                                type="text"
                                name="name.ar"
                                value={formik.values.name.ar}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="input p-2 my-1 border shadow-md rounded-md w-full"
                            />
                            {formik.touched.name?.ar && formik.errors.name?.ar && (
                                <p className="text-red-500">{formik.errors.name.ar}</p>
                            )}
                        </div>

                        {/* Title */}
                        <div>
                            <label>Title (EN)</label>
                            <input
                                type="text"
                                name="title.en"
                                value={formik.values.title.en}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="input p-2 my-1 border shadow-md rounded-md w-full"
                            />
                            {formik.touched.title?.en && formik.errors.title?.en && (
                                <p className="text-red-500">{formik.errors.title.en}</p>
                            )}
                        </div>
                        <div>
                            <label>المسمى (AR)</label>
                            <input
                                type="text"
                                name="title.ar"
                                value={formik.values.title.ar}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="input p-2 my-1 border shadow-md rounded-md w-full"
                            />
                            {formik.touched.title?.ar && formik.errors.title?.ar && (
                                <p className="text-red-500">{formik.errors.title.ar}</p>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <label>Description (EN)</label>
                            <textarea
                                name="description.en"
                                value={formik.values.description.en}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="input p-2 my-1 border shadow-md rounded-md w-full"
                            />
                            {formik.touched.description?.en && formik.errors.description?.en && (
                                <p className="text-red-500">{formik.errors.description.en}</p>
                            )}
                        </div>
                        <div>
                            <label>الوصف (AR)</label>
                            <textarea
                                name="description.ar"
                                value={formik.values.description.ar}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="input p-2 my-1 border shadow-md rounded-md w-full"
                            />
                            {formik.touched.description?.ar && formik.errors.description?.ar && (
                                <p className="text-red-500">{formik.errors.description.ar}</p>
                            )}
                        </div>

                        {/* Profile Image Upload */}
                        <div className="col-span-2 space-y-2">
                            <label className="block text-sm font-medium">Profile Image</label>
                            <div
                                className={`w-full h-60 border-2 border-dashed border-gray-300 rounded-md cursor-pointer transition overflow-hidden ${formik.values.profileImage ? "" : "flex flex-col justify-center items-center text-center hover:bg-gray-50"
                                    }`}
                                onClick={() => fileInputRef.current.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                            >
                                {formik.values.profileImage ? (
                                    <img
                                        src={formik.values.profileImage}
                                        alt="Preview"
                                        className="object-cover w-full h-full"
                                    />
                                ) : (
                                    <>
                                        <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 0 1 4-4h3m4 0h3a4 4 0 1 1 0 8H7a4 4 0 0 1 0-8h1" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 12v9m0 0l-3-3m3 3l3-3" />
                                        </svg>
                                        <p className="text-gray-600 text-sm">Click or drag file here to upload</p>
                                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, JPEG, WEBP, SVG , avif only</p>
                                    </>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml , image/Avif"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                            </div>
                            {formik.touched.profileImage && formik.errors.profileImage && (
                                <p className="text-red-500">{formik.errors.profileImage}</p>
                            )}
                        </div>

                        {/* Social Media */}
                        {["facebook", "instagram", "linkedin", "twitter"].map((platform) => (
                            <div key={platform}>
                                <label>{platform.charAt(0).toUpperCase() + platform.slice(1)}</label>
                                <input
                                    type="text"
                                    name={`socialMedia.${platform}`}
                                    value={formik.values.socialMedia[platform]}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className="input p-2 my-1 border shadow-md rounded-md w-full"
                                />
                                {formik.touched.socialMedia?.[platform] &&
                                    formik.errors.socialMedia?.[platform] && (
                                        <p className="text-red-500">{formik.errors.socialMedia[platform]}</p>
                                    )}
                            </div>
                        ))}
                    </div>

                    {/* Submit Buttons */}
                    <div className="border-t pt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-red-700 hover:bg-red-600 text-white px-6 py-2 rounded shadow"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-black text-white hover:scale-105 transition-transform px-6 py-2 rounded shadow"
                        >
                            {formik.isSubmitting ? "Submitting..." : `${trainerToEdit ? "Edit Trainer" : "Add Trainer"}`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddTrainerModal;
